import React, { memo, useCallback, useMemo } from 'react';
import type { ReportTeamRow } from '../../types';
import type { CallDirectionFilter } from '../reports-sub-header';
import { getReportsColumnDefinitions } from './column-definitions';
import './reports-incoming-table.scss';
import TanStackTable from '../../../../../../../../shared/design-system/ui/tanstack-table';
import type {
  PaginationOptionsChangePayload,
  SortConfig,
  TanStackTableProps,
} from '../../../../../../../../shared/design-system/ui/tanstack-table/types';

export type ReportsTableVariant = 'accordion' | 'flat';

interface IProps {
  data: ReportTeamRow[];
  isLoading?: boolean;
  /** 'accordion' = expandable team rows, 'flat' = simple rows (individual view) */
  variant?: ReportsTableVariant;
  sort?: SortConfig | null;
  onSort?: TanStackTableProps<ReportTeamRow>['onSort'];
  callDirection: CallDirectionFilter;
  paginationOptions?: {
    page: number;
    limit: number;
    totalElements: number;
  };
  onPaginationOptionsChange?: (options: {
    page?: number;
    limit?: number;
    totalElements?: number;
  }) => void;
}

const ReportsIncomingTable: React.FC<IProps> = memo(
  ({
    data = [],
    isLoading = false,
    variant = 'accordion',
    sort = null,
    onSort = null,
    callDirection,
    paginationOptions,
    onPaginationOptionsChange,
  }) => {
    const isAccordion = variant === 'accordion';

    const columns = useMemo(
      () =>
        getReportsColumnDefinitions({
          variant: isAccordion ? 'accordion' : 'flat',
          sort,
          onSort,
          callDirection,
        }),
      [isAccordion, sort, onSort, callDirection],
    );

    const pinnedColumns = useMemo(
      () => (isAccordion ? ['expander', 'teamName'] : ['teamName']),
      [isAccordion],
    );

    const handlePaginationChange = useCallback(
      (payload: PaginationOptionsChangePayload) => {
        if (onPaginationOptionsChange) {
          onPaginationOptionsChange({
            page: payload.page ?? paginationOptions?.page ?? 1,
            limit: payload.limit ?? paginationOptions?.limit ?? 25,
            totalElements:
              paginationOptions?.totalElements ?? data?.length ?? 0,
          });
        }
      },
      [onPaginationOptionsChange, paginationOptions, data?.length],
    );

    const totalRows = useMemo(() => {
      if (paginationOptions?.totalElements != null) {
        return paginationOptions.totalElements;
      }
      return data.length;
    }, [paginationOptions?.totalElements, data.length]);

    const paginationOptionsForTable = useMemo(
      () =>
        paginationOptions && onPaginationOptionsChange
          ? {
              page: paginationOptions.page || 1,
              limit: paginationOptions.limit || 25,
              totalRows,
              onPaginationOptionsChange: handlePaginationChange,
            }
          : undefined,
      [
        paginationOptions,
        onPaginationOptionsChange,
        totalRows,
        handlePaginationChange,
      ],
    );

    const paginationSizeOptions = useMemo(() => [25, 50, 100], []);

    return (
      <div className="dialer-reports-incoming-table">
        <TanStackTable<ReportTeamRow>
          key={callDirection}
          columns={columns}
          rows={isLoading ? [] : data}
          isLoading={isLoading}
          enableExpandedRows={isAccordion}
          getSubRows={(row) => row.subRows}
          pinnedColumns={pinnedColumns}
          defaultPinnedColumns={pinnedColumns}
          enableSorting={true}
          enableRowSelection={false}
          enableColumnResizing={false}
          sort={sort}
          onSort={onSort}
          tableClassName="dialer-reports-tanstack-table"
          paginationOptions={paginationOptionsForTable}
          paginationSizeOptions={paginationSizeOptions}
        />
      </div>
    );
  },
);

ReportsIncomingTable.displayName = 'ReportsIncomingTable';

export default ReportsIncomingTable;
